import React, { useCallback, useEffect, useState } from "react";
import SimplePopover from "../../reusable-components/SimplePopover/SimplePopover";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MailReceivedFromApiService } from "../../services/mail-received-from/mail-received-from-api.service";
import { MailReceivedFromDTO, MailReceivedFromFilter, MailReceivedFromRef } from "../../models/mail-received-from";
import { Button, Grid, List, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MailInboxDTO, MailInboxRef } from "src/models/mail-inbox";
import SuggestedByAiHint from "src/views/manage-mail/SuggestedByAiHint";

interface Props {
  suggested: MailReceivedFromRef | null;
  handlePickSuggestion: (receivedFrom: MailReceivedFromDTO) => void;
  searchQuery: string;
  mailInbox: MailInboxRef;
}

interface State {
  loading: boolean;
  error: string;
  items: MailReceivedFromDTO[];
  closePopover: boolean;
}

const ReceivedFromSuggestions = ({ suggested, handlePickSuggestion, searchQuery, mailInbox }: Props) => {
  const tRP = "components.edit_incoming_mail.suggestions";
  const { t } = useTranslation();
  const theme = useTheme();

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    closePopover: false,
  });

  const loadData = useCallback(
    async (signal: AbortSignal) => {
      setState((state) => ({
        ...state,
        loading: true,
      }));

      try {
        const response = await new MailReceivedFromApiService().loadItems(
          {
            // notIds: suggested?.id ? [suggested.id] : [],
            mailInboxIds: mailInbox ? [mailInbox.id] : [],
          } as MailReceivedFromFilter,
          undefined,
          [],
          {
            pageNumber: 0,
            pageSize: 10000,
          },
          false,
          60
        );
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          setState((state) => ({
            ...state,
            items: response.items,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    },
    [suggested?.id, searchQuery, mailInbox]
  );

  useEffect(() => {
    const abortController = new AbortController();
    loadData(abortController.signal);

    return () => abortController.abort();
  }, [mailInbox]);

  return (
    <>
      <SimplePopover
        buttonType={"icon"}
        buttonContent={
          <Tooltip title={`${t(`${tRP}.button_title`)}`}>
            <FontAwesomeIcon icon={faCaretDown} />
          </Tooltip>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Grid style={{ width: "500px" }}>
          <List style={{ listStyleType: "disc" }}>
            {state.items.length === 0 ? (
              <Typography>{`${t(`common_words.no_items`)}`}</Typography>
            ) : (
              state.items.map((item, idx) => (
                <Button
                  key={idx}
                  onClick={() => {
                    handlePickSuggestion(item);
                    setState((state) => ({ ...state, closePopover: true }));
                  }}
                >
                  <Grid
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      textTransform: "none",
                      color: theme.palette.text.primary,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <Typography>{item.name}</Typography>
                    <Typography
                      variant="body2"
                      color={theme.palette.primary.main}
                    >
                      {item.id === suggested?.id ? <SuggestedByAiHint /> : ""}
                    </Typography>
                  </Grid>
                </Button>
              ))
            )}
          </List>
        </Grid>
      </SimplePopover>
    </>
  );
};

export default ReceivedFromSuggestions;
